/**
 * Componente para exibir cards de projetos e demandas sem projeto
 * Dashboard específico para o Gestor
 * 
 * @author Equipe GestaoeCRM
 * @date 03/10/2025
 * @updated 08/10/2025 - Adicionado suporte para demandas sem projeto
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { projectCardsApi, CardItem } from '@/services/projectCardsApi';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { FolderOpen, Clock, User, Building2, Calendar, AlertCircle, ClipboardList, ChevronLeft, ChevronRight } from 'lucide-react';
import ProjectActiveOSGrid from './ProjectActiveOSGrid';
import DemandActiveOSGrid from './DemandActiveOSGrid';

interface ProjectCardsListProps {
  onProjectSelect?: (projectId: string) => void;
}

export default function ProjectCardsList({ onProjectSelect }: ProjectCardsListProps) {
  const { user } = useAuth();
  const [selectedItem, setSelectedItem] = useState<{ id: string; name: string; type: 'project' | 'demand' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  // Buscar projetos e demandas sem projeto com contagem de jobs/tasks ativos
  const { 
    data: cardsData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['projectsAndDemandsCards'],
    queryFn: () => projectCardsApi.getProjectsAndDemandsCards(),
    enabled: user?.role === 'GESTOR',
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });

  const handleItemClick = (item: CardItem) => {
    setSelectedItem({ id: item.id, name: item.name, type: item.type });
    onProjectSelect?.(item.id);
  };

  const handleBackToCards = () => {
    setSelectedItem(null);
    onProjectSelect?.(null);
  };

  // Funções de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil((cardsData?.data?.length || 0) / itemsPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Se um item foi selecionado, mostrar a grade de jobs/tasks ativos
  if (selectedItem) {
    if (selectedItem.type === 'project') {
      return (
        <ProjectActiveOSGrid 
          projectId={selectedItem.id}
          projectName={selectedItem.name}
          onBack={handleBackToCards}
        />
      );
    } else {
      return (
        <DemandActiveOSGrid 
          demandId={selectedItem.id}
          demandName={selectedItem.name}
          onBack={handleBackToCards}
        />
      );
    }
  }

  // Renderizar loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // Renderizar erro
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="h-12 w-12 mb-4" />
        <p className="text-lg font-medium mb-2">Erro ao carregar projetos</p>
        <p className="text-sm text-center mb-4">
          Não foi possível carregar os projetos. Tente novamente.
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  const cards = cardsData?.data || [];
  
  // Calcular paginação
  const totalPages = Math.ceil(cards.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCards = cards.slice(startIndex, endIndex);
  
  const projectCount = cards.filter(c => c.type === 'project').length;
  const demandCount = cards.filter(c => c.type === 'demand').length;

  // Renderizar mensagem quando não há cards
  if (cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <FolderOpen className="h-12 w-12 mb-4" />
        <p className="text-lg font-medium mb-2">Nenhum projeto ou demanda encontrado</p>
        <p className="text-sm text-center">
          Você não possui projetos ou demandas atribuídas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Meus Projetos e Demandas
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {projectCount > 0 && `${projectCount} projeto${projectCount !== 1 ? 's' : ''}`}
            {projectCount > 0 && demandCount > 0 && ' e '}
            {demandCount > 0 && `${demandCount} demanda${demandCount !== 1 ? 's' : ''} sem projeto`}
            {' atribuído' + (cards.length !== 1 ? 's' : '') + ' a você'}
          </p>
        </div>
        <div className="text-sm text-gray-500">
          Clique em um card para ver os jobs/tasks ativos
        </div>
      </div>

      {/* Grid de Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {currentCards.map((card) => {
          // Badge de tipo de card (Projeto ou Demanda)
          const typeIcon = card.type === 'project' ? (
            <FolderOpen className="h-3 w-3 mr-1 text-blue-600" />
          ) : (
            <ClipboardList className="h-3 w-3 mr-1 text-purple-600" />
          );
          
          const typeLabel = card.type === 'project' ? 'Projeto' : 'Demanda';
          const typeBgColor = card.type === 'project' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700';
          
          return (
            <div
              key={`${card.type}-${card.id}`}
              onClick={() => handleItemClick(card)}
              className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
            >
              {/* Card Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Badge de Tipo */}
                    <div className="flex items-center mb-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeBgColor}`}>
                        {typeIcon}
                        {typeLabel}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                      {card.name}
                    </h3>
                    {card.description && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {card.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      card.status === 'ATIVO' || card.status === 'EM_ANDAMENTO'
                        ? 'bg-green-100 text-green-800'
                        : card.status === 'PAUSADO' || card.status === 'PAUSADA'
                        ? 'bg-yellow-100 text-yellow-800'
                        : card.status === 'CONCLUIDO' || card.status === 'CONCLUIDA'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {card.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                {/* Informações específicas por tipo */}
                {card.type === 'project' && (
                  <div className="flex items-center mb-4">
                    <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600 truncate">
                      {card.client.name}
                    </span>
                  </div>
                )}
                
                {card.type === 'demand' && (
                  <>
                    {/* Badge de Prioridade */}
                    {card.priority && (
                      <div className="flex items-center mb-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          card.priority === 'CRITICA'
                            ? 'bg-red-100 text-red-800'
                            : card.priority === 'ALTA'
                            ? 'bg-orange-100 text-orange-800'
                            : card.priority === 'MEDIA'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          Prioridade: {card.priority}
                        </span>
                      </div>
                    )}
                    
                    {/* Responsável */}
                    {card.assignee && (
                      <div className="flex items-center mb-4">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600 truncate">
                          {card.assignee.name}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Contadores de OS */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <Clock className="h-4 w-4 text-green-600 mr-1" />
                      <span className="text-xl font-bold text-green-600">
                        {card.runningOSCount || 0}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Jobs/Tasks Ativos</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <FolderOpen className="h-4 w-4 text-gray-600 mr-1" />
                      <span className="text-xl font-bold text-gray-600">
                        {card.totalOSCount}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">Total de OS</p>
                  </div>
                </div>

                {/* Informações adicionais */}
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    <span>Criado em {new Date(card.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                  {card.manager && (
                    <div className="flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      <span>Gerido por {card.manager.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Footer */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 rounded-b-lg">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Clique para ver detalhes
                  </span>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Controles de Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 px-4 py-3 bg-gray-50 rounded-lg">
          {/* Informações da página */}
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Página {currentPage} de {totalPages} 
              ({cards.length} {cards.length === 1 ? 'item' : 'itens'})
            </span>
          </div>

          {/* Controles de navegação */}
          <div className="flex items-center space-x-2">
            {/* Botão Anterior */}
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </button>

            {/* Números das páginas */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            {/* Botão Próximo */}
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              Próximo
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
